export interface BonusPayableMonth {
  description: string;
  month: Date;
}

export interface BonusStatusByMonth {
  description: string;
  month: Date;
  stats: BonusStats;
}

export interface BonusStatByMonth {
  description: string;
  heading: string;
  icon: string;
  name: string;
  payAmount: number;
}

export interface BonusStats {
  accidentCount: number;
  commitmentStats: CommitmentStats[];
  criticalEventCount: number;
  finalCommitBonus: boolean;
  finalOnTimeBonus: boolean;
  finalOnTimeBonusText: string;
  finalSafetyBonus: boolean;
  finalSafetyBonusText: string;
  finalTotalBonus: number;
  lastUpdatedDate: Date;
  lateArrivalCount: number;
  monthProgress: number;
  qualifyPeriod: Date;
  safetyVideoCompletion: number;
  safetyVideoNotAssigned: boolean;
  wigAttendanceCounts: number;
  wigAttendanceDenominator: number;
}

export interface CommitmentStats {
  commitmentCount: number;
  commitmentDenominator: number;
  leadSubject: string;
}

export interface OptInStatus {
  driver: string;
  expirationDate: Date;
  profileStatus: string;
  startDate: Date;
  status: string;
}

export interface Month {
  description?: string;
  endDate: Date;
  month: string;
  startDate: Date;
}

export enum DisplayableType {
  None = '',
  Previous = 'Previous',
  Next = 'Next',
  Both = 'Both',
}

export interface IDayDetail {
  date: Date;
  driverDailyLeadId: number;
  status: string;
  leadId: number;
  isDayOf: boolean;
  leadQuestions: LeadQuestions;
}

export interface LeadQuestions {
  leadId: number;
  questions: LeadQuestion[];
}

export interface LeadQuestion {
  question: string;
  questionType: string;
  stringAnswerMaxLength: number;
  possibleResponses: string[];
  index: number;
}

export class DayDetail implements IDayDetail {
  date: Date;
  disabled: boolean;
  driverDailyLeadId: number;
  icon: string;
  iconConfiguration: string[];
  index: number;
  selected: boolean;
  status: string;

  leadId: number;
  isDayOf: boolean;
  leadQuestions: LeadQuestions;

  constructor(dayDetail: IDayDetail) {
    this.date = dayDetail.date;
    this.driverDailyLeadId = dayDetail.driverDailyLeadId;
    this.status = dayDetail.status;
    this.disabled = dayDetail.status.trim().toLowerCase() !== 'can respond';
    this.icon = this.getCommitmentIcon(this.status);
    this.leadId = dayDetail.leadId;
    this.isDayOf = dayDetail.isDayOf;
    this.leadQuestions = dayDetail.leadQuestions;
  }

  private getCommitmentIcon(status: string) {
    let result: string;

    switch (status.trim().toLowerCase()) {
      case 'yes':
      case 'not applicable':
        result = 'check';
        break;
      case 'no':
        result = 'times';
        break;
      default:
        result = '';
    }

    return result;
  }
}

export abstract class CommitmentIcon {
  getIcon(leadSubject) {
    if (leadSubject && leadSubject.length > 0) {
      switch (leadSubject.toLowerCase().trim()) {
        case 'daily 5':
          return 'tasks';
        case 'creep mode':
          return 'tachometer-alt-slowest';
        case 'g.o.a.l.':
          return 'eye';
        case 'no fatigued driving':
          return 'alarm-snooze';
        case 'no distracted driving':
          return 'steering-wheel';
        case 'trip plan':
          return 'map-marked-alt';
        case 'proper speed':
          return 'tachometer-alt-fastest';
        case 'lane changes':
          return 'road';
        case 'following distance':
          return 'arrows-alt-h';
        case 'equipment inspection':
          return 'search';
        case '3 points of contact':
          return 'share-alt';
        case 'marco replies':
          return 'reply-all';
      }
    }
    return 'clipboard-check';
  }
}

export interface IDriverStats {
  accidentCount: number;
  commitBonusPercentage: number;
  commitmentStats: ICommitmentStat[];
  criticalEventCount: number;
  finalCommitBonus: boolean;
  finalCommitBonusText: string;
  finalOnTimeBonus: boolean;
  finalOnTimeBonusText: string;
  finalSafetyBonus: boolean;
  finalSafetyBonusText: string;
  finalTotalBonus: number;
  lastUpdatedDate: Date;
  lateArrivalCount: number;
  monthProgress: number;
  qualifyPeriod: Date;
  safetyVideoCompletion: number;
  safetyVideoNotAssigned: boolean;
  wigAttendanceCounts: number;
  wigAttendanceDenominator: number;
  dvirCount: number;
  paperworkCount: number;
  csaViolationCount: number;
  logViolationCount: number;
}

export interface ICommitmentStat {
  commitmentCount: number;
  commitmentDenominator: number;
  leadSubject: string;
  leadId: number;
}

export class CommitmentStat extends CommitmentIcon implements ICommitmentStat {
  commitmentCount: number;
  commitmentDenominator: number;
  icon: string;
  leadSubject: string;
  leadId: number;

  constructor(commitmentStat: ICommitmentStat) {
    super();
    this.commitmentCount = commitmentStat.commitmentCount;
    this.commitmentDenominator = commitmentStat.commitmentDenominator;
    this.leadSubject = commitmentStat.leadSubject;
    this.leadId = commitmentStat.leadId;
    this.icon = this.getIcon(this.leadSubject);
  }
}

export class DriverStats implements IDriverStats {
  accidentCount: number;
  commitBonusPercentage: number;
  commitmentStats: ICommitmentStat[];
  criticalEventCount: number;
  finalCommitBonus: boolean;
  finalCommitBonusText: string;
  finalOnTimeBonus: boolean;
  finalOnTimeBonusText: string;
  finalSafetyBonus: boolean;
  finalSafetyBonusText: string;
  finalTotalBonus: number;
  lastUpdatedDate: Date;
  lateArrivalCount: number;
  monthProgress: number;
  qualifyPeriod: Date;
  safetyVideoCompletion: number;
  safetyVideoNotAssigned: boolean;
  wigAttendanceCounts: number;
  wigAttendanceDenominator: number;
  dvirCount: number;
  paperworkCount: number;
  csaViolationCount: number;
  logViolationCount: number;

  constructor(driverStat: IDriverStats) {
    this.accidentCount = driverStat.accidentCount;
    this.commitBonusPercentage = driverStat.commitBonusPercentage;
    this.commitmentStats = driverStat.commitmentStats.map((x) => new CommitmentStat(x));
    this.criticalEventCount = driverStat.criticalEventCount;
    this.finalCommitBonus = driverStat.finalCommitBonus;
    this.finalCommitBonusText = driverStat.finalCommitBonusText;
    this.finalOnTimeBonus = driverStat.finalOnTimeBonus;
    this.finalOnTimeBonusText = driverStat.finalOnTimeBonusText;
    this.finalSafetyBonus = driverStat.finalSafetyBonus;
    this.finalSafetyBonusText = driverStat.finalSafetyBonusText;
    this.finalTotalBonus = driverStat.finalTotalBonus;
    this.lastUpdatedDate = driverStat.lastUpdatedDate;
    this.lateArrivalCount = driverStat.lateArrivalCount;
    this.monthProgress = driverStat.monthProgress;
    this.qualifyPeriod = driverStat.qualifyPeriod;
    this.safetyVideoCompletion = driverStat.safetyVideoCompletion;
    this.safetyVideoNotAssigned = driverStat.safetyVideoNotAssigned;
    this.wigAttendanceCounts = driverStat.wigAttendanceCounts;
    this.wigAttendanceDenominator = driverStat.wigAttendanceDenominator;
    this.dvirCount = driverStat.dvirCount;
    this.paperworkCount = driverStat.paperworkCount;
    this.csaViolationCount = driverStat.csaViolationCount;
    this.logViolationCount = driverStat.logViolationCount;
  }
}
